import styled from '@emotion/styled';
import { Div, Heading, P } from '@kizi/core-components';
import { Checkbox, CheckboxProps } from '@kizi/shared-components';
import React from 'react';
import {
  MainCoveragePrice,
  MainCoveragePriceProps,
} from '../MainCoverage/Price';

/*
  Add word-break: break-word; if word breaking issue [6436]
*/
const Title = styled(Heading)`
  grid-area: title;
  hyphens: auto;
`;

export interface AdditionalCoverageItemProps {
  title: string;
  subTitle: string;
  checkbox: CheckboxProps;
  price: MainCoveragePriceProps;
  advise?: {
    bg?: string;
    color?: string;
    text: string;
  };
}

export const AdditionalCoverageItem: React.VFC<AdditionalCoverageItemProps> = ({
  title,
  subTitle,
  price,
  checkbox,
  advise,
}) => {
  const handleChange = React.useCallback(() => {
    checkbox.onChange?.({
      target: {
        name: checkbox.name as string,
        value: checkbox.value as string,
        checked: !checkbox.checked,
      },
    } as React.ChangeEvent<any>);
  }, [checkbox]);
  const handleClick = React.useCallback<
    React.MouseEventHandler<HTMLDivElement>
  >(
    (event) => {
      if (event.target instanceof HTMLInputElement) {
        return;
      }
      handleChange();
    },
    [handleChange],
  );

  return (
    <Div
      position="relative"
      variant="primaryCard"
      padding={[3, null, 6]}
      display="grid"
      alignContent="baseline"
      gridRowGap={1}
      gridColumnGap={[3, null, 6]}
      gridTemplateAreas={
        advise
          ? `
        "input advise price"
        "input title price"
        "input subTitle price"
        "input . price"
        `
          : `
        "input title price"
        "input subTitle price"
        "input . price"
        `
      }
      gridTemplateColumns={'max-content auto max-content'}
      borderWidth={2}
      borderStyle="solid"
      borderColor={checkbox.checked ? 'main.1' : 'transparent'}
      onClick={handleClick}
      style={{
        cursor: 'pointer',
      }}
      data-testid="AdditionalCoverageItem"
    >
      <Div
        gridArea="input"
        display="flex"
        alignItems="center"
        justifyContent="center"
        data-testid="AdditionalCoverageItem.checkbox.wrapper"
      >
        <Checkbox
          variant="main"
          {...checkbox}
          mx={0}
          onChange={handleChange}
          data-testid="AdditionalCoverageItem.checkbox"
        />
      </Div>
      {advise && (
        <P
          justifySelf="flex-start"
          borderRadius={20}
          bg={advise.bg || 'supplementary.1'}
          color={advise.color || 'dark.1'}
          variant="captionImportant"
          py={1}
          px={2}
          mb={2}
          data-testid="AdditionalCoverageItem.advise"
        >
          {advise.text}
        </P>
      )}
      <Title
        as="h4"
        variant="H4"
        my={0}
        data-testid="AdditionalCoverageItem.title"
      >
        {title}
      </Title>
      <P
        gridArea="subTitle"
        variant="footnoteRegular"
        data-testid="AdditionalCoverageItem.subTitle"
      >
        {subTitle}
      </P>
      <MainCoveragePrice
        {...price}
        alignSelf={[null, null, 'center']}
        gridArea="price"
        data-testid="AdditionalCoverageItem.price"
      />
    </Div>
  );
};
